import React, { useState, useEffect } from 'react';
import { DollarSign, AlertTriangle, Shield, Info, ExternalLink, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

const PaymentInfoTab = () => {
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchPaymentInfo();
  }, []);

  const fetchPaymentInfo = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/payment-info`);
      if (response.ok) {
        const data = await response.json();
        setPaymentInfo(data);
        setLastUpdated(data.updated_at);
      }
    } catch (error) {
      console.error('Error fetching payment info:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Card className="border-4 border-red-500 shadow-lg">
          <CardContent className="py-12">
            <div className="flex items-center justify-center gap-3">
              <RefreshCw className="w-6 h-6 text-red-500 animate-spin" />
              <p className="text-gray-600 dark:text-gray-400">Loading payment information...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!paymentInfo) {
    return (
      <div className="space-y-4">
        <Card className="border-4 border-red-500 shadow-lg">
          <CardContent className="py-12">
            <p className="text-center text-gray-500">Payment information not available.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Main Payment Info Card with Red Border */}
      <Card className="border-4 border-red-500 shadow-2xl bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 dark:from-red-950/30 dark:via-orange-950/30 dark:to-yellow-950/30 overflow-hidden">
        {/* Warning Header Banner */}
        <div className="bg-gradient-to-r from-red-600 via-red-500 to-orange-500 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-full flex items-center justify-center shadow-lg">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 animate-pulse" />
                {paymentInfo.title}
              </h2>
              <p className="text-red-100 text-sm mt-1">
                Please read this important information carefully
              </p>
            </div>
          </div>
        </div>

        <CardContent className="p-6 space-y-6">
          {/* Summary Box */}
          <div className="bg-red-100 dark:bg-red-900/30 border-2 border-red-300 dark:border-red-700 rounded-xl p-5">
            <div className="flex items-start gap-3">
              <Shield className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-red-900 dark:text-red-200 text-lg mb-2">Quick Summary</h3>
                <p className="text-red-800 dark:text-red-300 leading-relaxed">
                  {paymentInfo.short_summary}
                </p>
              </div>
            </div>
          </div>

          {/* Detailed Content */}
          <div className="prose prose-sm sm:prose dark:prose-invert max-w-none">
            {paymentInfo.content.split('\n\n').map((section, index) => {
              // Check if section is a header (starts with emoji)
              const isHeader = section.match(/^[🔸⚠️💰📋]/);
              
              if (isHeader) {
                const lines = section.split('\n');
                const headerLine = lines[0];
                const contentLines = lines.slice(1);
                
                return (
                  <div key={index} className="mb-6">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 border-b-2 border-red-200 dark:border-red-800 pb-2 mb-3">
                      {headerLine}
                    </h3>
                    {contentLines.map((line, lineIdx) => {
                      if (line.startsWith('•')) {
                        return (
                          <div key={lineIdx} className="flex items-start gap-2 ml-4 mb-2">
                            <span className="text-red-500 font-bold mt-1">•</span>
                            <span className="text-gray-700 dark:text-gray-300">{line.substring(1).trim()}</span>
                          </div>
                        );
                      }
                      return line.trim() ? (
                        <p key={lineIdx} className="text-gray-700 dark:text-gray-300 mb-2">{line}</p>
                      ) : null;
                    })}
                  </div>
                );
              }
              
              // Check for bullet points
              if (section.includes('•')) {
                const lines = section.split('\n');
                return (
                  <div key={index} className="mb-4">
                    {lines.map((line, lineIdx) => {
                      if (line.startsWith('•')) {
                        return (
                          <div key={lineIdx} className="flex items-start gap-2 ml-4 mb-2">
                            <span className="text-red-500 font-bold mt-1">•</span>
                            <span className="text-gray-700 dark:text-gray-300">{line.substring(1).trim()}</span>
                          </div>
                        );
                      }
                      return line.trim() ? (
                        <p key={lineIdx} className="text-gray-700 dark:text-gray-300 mb-2">{line}</p>
                      ) : null;
                    })}
                  </div>
                );
              }
              
              return (
                <p key={index} className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                  {section}
                </p>
              );
            })}
          </div>

          {/* Footer Notice */}
          <div className="bg-gray-100 dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Info className="w-4 h-4 flex-shrink-0" />
              <p>
                <strong>This notice is permanent and cannot be dismissed.</strong> It applies to all users on this platform.
              </p>
            </div>
            {lastUpdated && (
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-2 pl-6">
                Last updated: {new Date(lastUpdated).toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Additional Info Card */}
      <Card className="border-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/50 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="font-bold text-amber-900 dark:text-amber-200 text-lg mb-2">
                Questions About Payments?
              </h3>
              <p className="text-amber-800 dark:text-amber-300 text-sm leading-relaxed">
                For any questions about payments, pricing, refunds, or transaction issues, 
                please contact your trainer directly. They are responsible for handling all 
                payment-related matters and can provide you with their specific policies and procedures.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentInfoTab;
